import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

/**
 * Tooltip — a reliable, styled replacement for the native `title=` attribute (which is slow ~1.5s, invisible on
 * touch, browser-inconsistent, and un-styleable). Shows on HOVER (short delay), on keyboard FOCUS, and on TOUCH
 * (tap toggles). Rendered through a PORTAL so it never clips inside an `overflow-hidden`/scroll container, with a
 * viewport-aware flip (above by default, below if there's no room). Monochrome, matches the design language.
 *
 * Usage: wrap the trigger element — `<Tooltip content="What this does"><button>…</button></Tooltip>`.
 * The trigger stays a single focusable element; we clone it to attach handlers + `aria-describedby`.
 */
interface Props {
  content: React.ReactNode;
  children: React.ReactElement;
  /** Preferred side; flips automatically if there isn't room. */
  side?: 'top' | 'bottom';
  /** Hover open delay in ms (0 = instant). Default 120 — fast but not jumpy. */
  delay?: number;
  className?: string;
}

const GAP = 8; // px between the trigger and the bubble

const Tooltip: React.FC<Props> = ({ content, children, side = 'top', delay = 120, className }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number; placement: 'top' | 'bottom' } | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();

  const clearTimer = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const show = useCallback(() => { clearTimer(); timer.current = setTimeout(() => setOpen(true), delay); }, [delay]);
  const hide = useCallback(() => { clearTimer(); setOpen(false); }, []);

  // Position the bubble relative to the trigger once it's open (and on scroll/resize while open).
  useLayoutEffect(() => {
    if (!open) { setCoords(null); return; }
    const place = () => {
      const t = triggerRef.current?.getBoundingClientRect();
      const b = bubbleRef.current?.getBoundingClientRect();
      if (!t) return;
      const bh = b?.height ?? 32;
      const bw = b?.width ?? 0;
      // Flip to bottom if there isn't room above.
      const placement: 'top' | 'bottom' = side === 'top' && t.top - bh - GAP < 4 ? 'bottom' : side;
      const top = placement === 'top' ? t.top - bh - GAP : t.bottom + GAP;
      // Center horizontally, clamped into the viewport.
      let left = t.left + t.width / 2 - bw / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - bw - 8));
      setCoords({ left, top, placement });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', place, true); window.removeEventListener('resize', place); };
  }, [open, side, content]);

  const trigger = React.cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      // Preserve any ref the child already had (function or object ref).
      const r = (children as unknown as { ref?: React.Ref<HTMLElement> }).ref;
      if (typeof r === 'function') r(node); else if (r && typeof r === 'object') (r as React.MutableRefObject<HTMLElement | null>).current = node;
    },
    'aria-describedby': open ? id : undefined,
    onMouseEnter: (e: React.MouseEvent) => { show(); children.props.onMouseEnter?.(e); },
    onMouseLeave: (e: React.MouseEvent) => { hide(); children.props.onMouseLeave?.(e); },
    onFocus: (e: React.FocusEvent) => { setOpen(true); children.props.onFocus?.(e); },
    onBlur: (e: React.FocusEvent) => { hide(); children.props.onBlur?.(e); },
    // Touch: briefly SHOW the tooltip as an informational glance (native title never shows on touch at all), then
    // auto-dismiss — a tap does NOT block the trigger's own onClick, and it can never get stuck open.
    onTouchStart: (e: React.TouchEvent) => {
      clearTimer(); setOpen(true);
      timer.current = setTimeout(() => setOpen(false), 1500);
      children.props.onTouchStart?.(e);
    },
  });

  // Clean up any pending timer on unmount so a late fire can't setState on a gone component.
  useEffect(() => () => clearTimer(), []);

  return (
    <>
      {trigger}
      {open && content != null && coords != null && createPortal(
        <div
          ref={bubbleRef}
          id={id}
          role="tooltip"
          style={{ position: 'fixed', left: coords.left, top: coords.top, zIndex: 100 }}
          className={cn(
            'pointer-events-none max-w-[260px] rounded-lg px-2.5 py-1.5 text-xs leading-snug shadow-lg',
            'bg-secondary-900 text-white dark:bg-secondary-100 dark:text-secondary-900',
            className,
          )}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
};

export default Tooltip;
